import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause, FaTimes } from "react-icons/fa";

const FileGridUploader = ({
  files = [],
  setFiles,
  allowMultiple = true,
  acceptedFileTypes = "video/*,image/*",
  maxFiles = 12,
}) => {
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...newFiles].slice(0, maxFiles));
  };

  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full">
      {/* Upload button */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
          onClick={() => inputRef.current?.click()}
        >
          Add Files
        </button>
        
        <input
          ref={inputRef}
          type="file"
          multiple={allowMultiple}
          accept={acceptedFileTypes}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* File grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {files.map((fileObj, index) => {
          const isVideo = fileObj.file.type.startsWith("video/");
          return (
            <div
              key={index}
              className="relative group rounded-lg overflow-hidden border border-gray-200"
            >
              {isVideo ? (
                <VideoPreview
                  file={fileObj}
                  onRemove={() => handleRemove(index)}
                />
              ) : (
                <ImagePreview
                  file={fileObj}
                  onRemove={() => handleRemove(index)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FileGridUploader;

//
// ---- Video Preview ----
//
const VideoPreview = ({ file, onRemove }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  // Update progress bar
  const handleTimeUpdate = () => {
    const current = videoRef.current?.currentTime || 0;
    const duration = videoRef.current?.duration || 1;
    setProgress((current / duration) * 100);
  };

  // Toggle play/pause
  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;

    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
    } else {
      vid
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };

  // Allow progress dragging
  const handleSeek = (e) => {
    const vid = videoRef.current;
    if (!vid) return;
    const rect = e.target.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * vid.duration;
    vid.currentTime = newTime;
  };

  // Stop video when unmounted
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = "";
      }
    };
  }, []);

  return (
    <div className="relative w-full h-48 bg-black rounded-lg overflow-hidden">
      <video
        ref={videoRef}
        src={file.url}
        className="w-full h-full object-cover"
        onTimeUpdate={handleTimeUpdate}
      />

      {/* Play/Pause button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
        }}
        className="absolute top-2 left-2 bg-black/50 p-2 rounded-full text-white z-10"
      >
        {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
      </button>

      {/* Remove button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-2 right-2 bg-black/50 p-2 rounded-full text-white z-10"
      >
        <FaTimes size={14} />
      </button>

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 w-full h-1 bg-gray-300 cursor-pointer"
        onClick={handleSeek}
      >
        <div
          className="h-full bg-purple-600"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

//
// ---- Image Preview ----
//
const ImagePreview = ({ file, onRemove }) => (
  <div className="relative w-full h-48">
    <img
      src={file.url}
      alt="preview"
      className="w-full h-full object-cover rounded-lg"
    />
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      className="absolute top-2 right-2 bg-black/50 p-2 rounded-full text-white z-10"
    >
      <FaTimes size={14} />
    </button>
  </div>
);
